import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Building2, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { errorMessage } from "@/lib/api";
import { homeFor } from "@/components/RequireRole";
import { FieldError, FullPageLoader, usePageTitle } from "@/components/States";
import { Brand } from "@/components/layout/SidebarShell";
import { WORKER_ROLE_OPTIONS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const FIELDS = [
  { k: "company_name", label: "Company name", auto: "organization" },
  { k: "full_name", label: "Your name", auto: "name" },
  { k: "email", label: "Email", type: "email", auto: "email" },
  { k: "password", label: "Password (min. 8 characters)", type: "password", auto: "new-password" },
];

function validate(f) {
  const e = {};
  if (f.company_name.trim().length < 2) e.company_name = "Enter your company name";
  if (f.full_name.trim().length < 2) e.full_name = "Enter your name";
  if (!EMAIL_RE.test(f.email.trim())) e.email = "Enter a valid email address";
  if (f.password.length < 8) e.password = "Use at least 8 characters";
  if (!f.enabled_user_types.length) e.enabled_user_types = "Choose at least one team type";
  return e;
}

export default function Signup() {
  usePageTitle("Sign up your company");
  const { profile, loading, signUp } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState({ company_name: "", full_name: "", email: "", password: "", enabled_user_types: [] });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  if (loading) return <FullPageLoader />;
  if (profile) return <Navigate to={homeFor(profile)} replace />;

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate(f);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    try {
      const me = await signUp({ ...f, email: f.email.trim(), company_name: f.company_name.trim(), full_name: f.full_name.trim() });
      navigate(homeFor(me), { replace: true });
    } catch (err) {
      setErrors({ form: errorMessage(err) });
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-900 p-6">
      <form onSubmit={submit} noValidate className="w-full max-w-md space-y-5 rounded-xl border border-slate-200 bg-white p-8 shadow-xl" data-testid="signup-form">
        <div className="inline-block rounded-lg bg-slate-900 p-3"><Brand roleLabel="For warrant teams" /></div>
        <div>
          <h1 className="font-display flex items-center gap-2 text-2xl font-extrabold text-slate-900"><Building2 className="h-6 w-6 text-blue-700" /> Sign up your company</h1>
          <p className="mt-1 text-sm text-slate-500">14-day free trial. No card needed to start.</p>
        </div>
        {FIELDS.map(({ k, label, type = "text", auto }) => (
          <div key={k}>
            <Label htmlFor={`su-${k}`}>{label}</Label>
            <Input id={`su-${k}`} type={type} autoComplete={auto} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="mt-1.5 h-11" data-testid={`signup-${k}-input`} />
            <FieldError message={errors[k]} testId={`signup-${k}-error`} />
          </div>
        ))}
        <fieldset className="space-y-2" data-testid="signup-user-types">
          <legend className="text-sm font-semibold text-slate-900">What type of team do you manage? *</legend>
          <p className="text-xs text-slate-500">Choose the types your company will use. You can update these later in Company settings.</p>
          <div className="grid gap-2 sm:grid-cols-2">
            {WORKER_ROLE_OPTIONS.map(([role, roleLabel]) => {
              const label = {
                agent: "Warrant Officers",
                locksmith: "Locksmiths",
                engineer: "Engineers",
                dog_handler: "Dog Handlers",
              }[role] || roleLabel;
              return (
              <label key={role} className="flex min-h-[44px] cursor-pointer items-center gap-3 rounded-md border border-slate-200 p-3">
                <Checkbox checked={f.enabled_user_types.includes(role)} onCheckedChange={(checked) => setF({
                  ...f,
                  enabled_user_types: checked ? [...f.enabled_user_types, role] : f.enabled_user_types.filter((value) => value !== role),
                })} data-testid={`signup-user-type-${role}`} />
                <span className="text-sm font-medium text-slate-800">{label}</span>
              </label>
              );
            })}
          </div>
          <FieldError message={errors.enabled_user_types} testId="signup-user-types-error" />
        </fieldset>
        {errors.form && <p role="alert" className="rounded-md bg-rose-50 p-3 text-sm text-rose-700" data-testid="signup-form-error">{errors.form}</p>}
        <Button type="submit" disabled={busy} className="h-11 w-full bg-blue-700 hover:bg-blue-800" data-testid="signup-submit-button">
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Start free trial
        </Button>
        <p className="text-center text-sm text-slate-600">Already have an account? <Link to="/login" className="font-semibold text-blue-700 hover:underline" data-testid="signup-login-link">Sign in</Link></p>
      </form>
    </div>
  );
}
